import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { format } from "date-fns";
import { SendIcon } from "lucide-react";
import type { z } from "zod";
import { CreateExpenseRecordSchema } from "@/modules/expenses/domain/contracts/expense-records";
import { useExpenseCategoriesQuery } from "../api/queries";
import { formatExpenseAmount } from "../lib/expense-format";
import { ExpenseFormSection } from "./ExpenseFormSection";

const ExpenseRecordFormSchema = CreateExpenseRecordSchema.omit({
  submit: true,
});
export type ExpenseRecordFormValues = z.infer<typeof ExpenseRecordFormSchema>;

type ExpenseRecordFormProps = {
  initialValues?: Partial<ExpenseRecordFormValues>;
  onSubmit: (values: ExpenseRecordFormValues, submit: boolean) => void;
  submitting?: boolean;
  mode?: "create" | "edit";
};

export function ExpenseRecordForm({
  initialValues,
  onSubmit,
  submitting,
  mode = "create",
}: ExpenseRecordFormProps) {
  const { data: categories } = useExpenseCategoriesQuery();

  const methods = RHF.useForm<ExpenseRecordFormValues>({
    resolver: zodResolver(ExpenseRecordFormSchema),
    defaultValues: {
      expenseDate:
        initialValues?.expenseDate ?? format(new Date(), "yyyy-MM-dd"),
      categoryId: initialValues?.categoryId ?? "",
      payeeName: initialValues?.payeeName ?? "",
      amount: initialValues?.amount ?? undefined,
      description: initialValues?.description ?? "",
      notes: initialValues?.notes ?? "",
      performanceEventId: initialValues?.performanceEventId ?? "",
    },
  });

  const amount = methods.watch("amount");
  const categoryOptions = (categories ?? [])
    .filter((category) => category.isActive)
    .map((category) => ({ value: category.id, label: category.name }));

  const parsedAmount = Number(amount);
  const amountPreview =
    Number.isFinite(parsedAmount) && parsedAmount > 0
      ? formatExpenseAmount(parsedAmount)
      : null;

  return (
    <FormRoot methods={methods} onSubmit={() => {}} className="flex flex-col">
      <div className="space-y-3 px-6 py-5">
        <ExpenseFormSection title="ຂໍ້ມູນການຈ່າຍ">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormInput
              name="expenseDate"
              label="ວັນທີຄ່າໃຊ້ຈ່າຍ"
              type="date"
              required
            />
            <FormSelect
              name="categoryId"
              label="ຫມວດ"
              options={categoryOptions}
              placeholder="ເລືອກຫມວດ"
            />
            <div className="sm:col-span-2">
              <FormInput
                name="payeeName"
                label="ຜູ້ຮັບເງິນ / ຮ້ານ"
                placeholder="ຊື່ຜູ້ຮັບເງິນ"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <FormNumber
                name="amount"
                label="ຈຳນວນ (ກີບ)"
                min={1}
                required
              />
              {amountPreview ? (
                <p className="mt-1.5 font-medium text-foreground/80 text-sm tabular-nums">
                  {amountPreview}
                </p>
              ) : null}
            </div>
          </div>
        </ExpenseFormSection>

        <ExpenseFormSection title="ລາຍລະອຽດ">
          <FormTextarea
            name="description"
            label="ລາຍລະອຽດ"
            placeholder="ລາຍລະອຽດຄ່າໃຊ້ຈ່າຍ"
            rows={3}
          />
          <FormTextarea
            name="notes"
            label="ຫມາຍເຫດ"
            placeholder="ຫມາຍເຫດ (ບໍ່ບັງຄັບ)"
            rows={2}
          />
        </ExpenseFormSection>
      </div>

      <div className="flex flex-col-reverse gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={submitting}
          onClick={methods.handleSubmit((values) => onSubmit(values, false))}
        >
          {mode === "edit" ? "ບັນທຶກ" : "ບັນທຶກຮ່າງ"}
        </Button>
        <Button
          type="button"
          disabled={submitting}
          onClick={methods.handleSubmit((values) => onSubmit(values, true))}
        >
          <SendIcon className="mr-2 size-4" />
          {submitting
            ? "ກຳລັງບັນທຶກ..."
            : mode === "edit"
              ? "ບັນທຶກ ແລະ ສົ່ງອະນຸມັດ"
              : "ສົ່ງອະນຸມັດ"}
        </Button>
      </div>
    </FormRoot>
  );
}
